import { UTCDate } from '@date-fns/utc';
import { subMonths } from 'date-fns';

import { GLOBAL_MIN_START_DATE } from '../utils/providerDateOverrides';

export type DateRangeOption = {
  value: string;
  label: string;
  hidden?: boolean;
  getDateRange?: (
    systemMaxDate: UTCDate,
    systemMinDate: UTCDate
  ) => { start: UTCDate; end: UTCDate };
};

export type DateOption = {
  value: string;
  label: string;
  hidden?: boolean;
  getDate?: (systemMaxDate: UTCDate) => UTCDate;
};

export const getDateRangeFromPreset = (
  presetValue: string | undefined,
  systemMax: UTCDate,
  systemMin: UTCDate
): { start: UTCDate; end: UTCDate } => {
  const preset = DateRangePresets.find((p) => p.value === presetValue);

  if (preset?.getDateRange) {
    return preset.getDateRange(systemMax, systemMin);
  }

  return { start: systemMin, end: systemMax };
};

export const DateRangePresets: DateRangeOption[] = [
  // Hidden presets for template-level date range overrides
  {
    value: 'posting-provider-range',
    label: 'Posting Provider Range',
    hidden: true,
    getDateRange: (systemMaxDate) => ({
      start: new UTCDate(GLOBAL_MIN_START_DATE),
      end: systemMaxDate,
    }),
  },
  // Visible presets
  {
    value: 'last-6-months',
    label: 'Last 6 Months',
    getDateRange: (systemMaxDate) => ({
      start: subMonths(systemMaxDate, 5) as UTCDate,
      end: systemMaxDate,
    }),
  },
  {
    value: 'last-year',
    label: 'Last Year',
    getDateRange: (systemMaxDate) => ({
      start: subMonths(systemMaxDate, 11) as UTCDate,
      end: systemMaxDate,
    }),
  },
  {
    value: 'last-2-years',
    label: 'Last 2 Years',
    getDateRange: (systemMaxDate) => ({
      start: subMonths(systemMaxDate, 23) as UTCDate,
      end: systemMaxDate,
    }),
  },
  {
    value: 'last-5-years',
    label: 'Last 5 Years',
    getDateRange: (systemMaxDate) => ({
      start: subMonths(systemMaxDate, 59) as UTCDate,
      end: systemMaxDate,
    }),
  },
  {
    value: 'last-10-years',
    label: 'Last 10 Years',
    getDateRange: (systemMaxDate) => ({
      start: subMonths(systemMaxDate, 119) as UTCDate,
      end: systemMaxDate,
    }),
  },
  {
    value: 'max',
    label: 'Max',
    getDateRange: (systemMaxDate, systemMinDate) => ({
      start: systemMinDate,
      end: systemMaxDate,
    }),
  },
  { value: 'custom', label: 'Custom Range' },
];

export const DatePresets: DateOption[] = [
  {
    value: '6-months-ago',
    label: '6 Months Ago',
    getDate: (systemMaxDate) => subMonths(systemMaxDate, 5) as UTCDate,
  },
  {
    value: '1-year-ago',
    label: '1 Year Ago',
    getDate: (systemMaxDate) => subMonths(systemMaxDate, 11) as UTCDate,
  },
  {
    value: '2-years-ago',
    label: '2 Years Ago',
    getDate: (systemMaxDate) => subMonths(systemMaxDate, 23) as UTCDate,
  },
  {
    value: '5-years-ago',
    label: '5 Years Ago',
    getDate: (systemMaxDate) => subMonths(systemMaxDate, 59) as UTCDate,
  },
  {
    value: '10-years-ago',
    label: '10 Years Ago',
    getDate: (systemMaxDate) => subMonths(systemMaxDate, 119) as UTCDate,
  },
  {
    value: 'latest',
    label: 'Latest',
    getDate: (systemMaxDate) => systemMaxDate,
  },
  { value: 'custom', label: 'Custom Date' },
];
